import React, { useState } from 'react';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import { useUpgradeOptions } from '../../../../utils/useUpgradeOptions';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';

interface DiscountSubDetail {
  heading: string;
  sub_heading: string;
  price?: string;
}

interface DiscountItem {
  heading: string;
  subheading?: string;
  price?: string;
  sub_details?: DiscountSubDetail[];
}

interface PlanSelected {
  type: string;
  target_package_uuid: string;
  plan: string;
  logname: string;
  total_payable: string;
  discounts: DiscountItem[];
  package_name: string;
  heading: string;
}

interface CurrentPlan {
  heading: string;
  plan: string;
  plan_duration: string;
}

interface DiscountResponse {
  plan_selected: PlanSelected;
  current_plan: CurrentPlan;
}

interface UpgradeMembershipProps {
  onClose: () => void;
  student_plan_uuid: string;
  loadStudentData: () => void;
  student_current_plan: string;
  is_prepaid: number;
  no_of_weeks_originally_enrolled_to: number;
  no_of_cycle_remaining: number;
  student_uuid: string;
  country?: string;
  set_notification_variable: (show: boolean, type: string, message: string) => void;
}

function UpgradeMembership({
  onClose,
  student_plan_uuid,
  loadStudentData,
  student_current_plan,
  is_prepaid,
  no_of_weeks_originally_enrolled_to,
  no_of_cycle_remaining,
  student_uuid,
  country = 'US',
  set_notification_variable
}: UpgradeMembershipProps) {
  const [upgradePackageUUID, setUpgradePackageUUID] = useState('');
  const [numberOfWeeks, setNumberOfWeeks] = useState('');
  const [discountData, setDiscountData] = useState<DiscountResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Upgrade targets + allowed cadences are derived from the active_packages catalog
  // (shared with the downgrade flow) so newer tiers — Consistency 1/2/3 and the hidden
  // 8-21 plans — appear automatically instead of from a hardcoded list.
  const {
    planOptions,
    weeksOptions,
    error: catalogError
  } = useUpgradeOptions({
    student_plan_uuid,
    selectedPackageUUID: upgradePackageUUID,
    no_of_weeks_originally_enrolled_to
  });

  const handlePackageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target?.value || '';
    setUpgradePackageUUID(value);
    setDiscountData(null);
    setErrorMessage('');
  };

  const handleWeeksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target?.value || '';
    setNumberOfWeeks(value);
    setDiscountData(null);
    setErrorMessage('');
  };

  const fetchDiscount = async () => {
    if (!upgradePackageUUID || !numberOfWeeks) {
      setErrorMessage('Please select an upgrade plan and number of weeks.');
      return;
    }

    // If the same plan is selected, enforce that the student is upgrading
    // to more weeks than their current enrollment.
    const isSamePlanWithInsufficientWeeks =
      upgradePackageUUID === student_plan_uuid &&
      Number(numberOfWeeks) <= Number(no_of_weeks_originally_enrolled_to || 0);

    if (isSamePlanWithInsufficientWeeks) {
      setErrorMessage(
        `Please select more than ${no_of_weeks_originally_enrolled_to} weeks for this plan.`
      );
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const apiUrl = get_api_url('payment/discount_for_package');
    const payload = {
      student_uuid,
      upgrade_package_uuid: upgradePackageUUID,
      number_of_weeks: Number(numberOfWeeks),
      utm_medium: 'desktop_web',
      app_platform: 'web_app',
      device_type: 'desktop'
    };

    try {
      const response = await post_api(apiUrl, payload, true);
      const data = response?.data;
      if (data?.plan_selected) {
        setDiscountData({
          plan_selected: data.plan_selected,
          current_plan: data.current_plan || {
            heading: 'Current plan',
            plan: student_current_plan,
            plan_duration: ''
          }
        });
      } else {
        // post_api resolves on any 2xx, so a 200 with an error-shaped body (no plan_selected)
        // lands here — surface the backend message instead of silently resetting the modal.
        setErrorMessage(data?.message || 'Could not load discount details. Please try again.');
        setDiscountData(null);
      }
    } catch (error) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        'Failed to load discount details. Please try again.';
      setErrorMessage(msg);
      setDiscountData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!discountData?.plan_selected) {
      setErrorMessage('Please fetch discount details first.');
      return;
    }

    setUpgrading(true);
    setErrorMessage('');

    const apiUrl = get_api_url('payment/membership_upgrade_v2');
    const payload = {
      student_uuid,
      upgrade_package_uuid: upgradePackageUUID,
      number_of_weeks: Number(numberOfWeeks),
      coupon_code: '',
      country: country || 'US',
      utm_medium: 'desktop_web',
      app_platform: 'web_app',
      device_type: 'desktop'
    };

    try {
      const response = await post_api(apiUrl, payload, true);
      if (response?.data?.status === 'success') {
        const results = response.data.results || {};
        const message = results.heading || 'Membership upgraded successfully.';
        set_notification_variable(true, 'success', message);
        loadStudentData();
        onClose();
      } else {
        // post_api resolves on any 2xx, so a 200 { status: 'failure', message } (e.g.
        // "override_no_of_cycle_remaining is not defined") never reaches catch — surface it,
        // otherwise the button just resets with no feedback and the operator retries blindly.
        const msg = response?.data?.message || 'Upgrade failed. Please try again.';
        setErrorMessage(msg);
        set_notification_variable(true, 'error', msg);
      }
    } catch (error) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        'Upgrade failed. Please try again.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <div className="w-full min-w-0 px-1">
      <hr className="mb-6 border-t border-gray-200" />

      {/* Current plan context — more breathing room */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Current plan</p>
        <p className="mt-1 text-base font-medium text-gray-800">
          {student_current_plan}
          {is_prepaid === 0 ? ' / Monthly' : ''}
        </p>
        {is_prepaid === 1 && (
          <p className="mt-1.5 text-sm text-gray-600">
            {no_of_weeks_originally_enrolled_to} weeks enrolled · {no_of_cycle_remaining} cycles
            left
          </p>
        )}
      </div>

      <div className="space-y-4">
        <CustomSingleSelect
          label="Upgrade to package"
          options={planOptions}
          onChange={handlePackageChange}
          name="upgrade_package_uuid"
          value={upgradePackageUUID}
          style={{ width: '100%' }}
        />

        <CustomSingleSelect
          label="Number of weeks"
          options={weeksOptions.map((w) => ({ label: w.toString(), value: w.toString() }))}
          onChange={handleWeeksChange}
          name="number_of_weeks"
          value={numberOfWeeks}
          style={{ width: '100%' }}
        />
      </div>

      {!discountData ? (
        <div className="mt-6">
          <button
            type="button"
            onClick={fetchDiscount}
            disabled={!upgradePackageUUID || !numberOfWeeks || loading}
            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Loading...' : 'Get discount details'}
          </button>
        </div>
      ) : (
        <>
          {/* Receipt-style summary — roomier layout */}
          <div className="mt-6 overflow-hidden rounded-xl border border-gray-300 bg-[#fafaf9] shadow-sm">
            <div className="border-b border-dashed border-gray-400 bg-white px-5 py-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Membership upgrade
              </p>
            </div>
            <div className="px-5 py-4 font-sans text-sm text-gray-800">
              {discountData.plan_selected.discounts?.map((d, i) => (
                <div key={i} className="mb-3 last:mb-0">
                  {d.heading && (
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {d.heading}
                    </p>
                  )}
                  {d.subheading && (
                    <div className="flex justify-between gap-2 border-b border-dotted border-gray-300 py-1">
                      <span className="text-gray-700">{d.subheading}</span>
                      {d.price ? (
                        <span className="shrink-0 font-medium tabular-nums text-gray-900">
                          {d.price}
                        </span>
                      ) : null}
                    </div>
                  )}
                  {!d.subheading && d.price && (
                    <div className="flex justify-between gap-2 border-b border-dotted border-gray-300 py-1">
                      <span className="text-gray-700">—</span>
                      <span className="shrink-0 font-medium tabular-nums text-gray-900">
                        {d.price}
                      </span>
                    </div>
                  )}
                  {d.sub_details?.length ? (
                    <div className="mt-1 space-y-0.5">
                      {d.sub_details.map((sd, j) => {
                        const isCredit =
                          typeof sd.price === 'string' && sd.price.trim().startsWith('-');
                        return (
                          <div
                            key={j}
                            className="flex justify-between gap-2 border-b border-dotted border-gray-300 py-1.5 text-gray-700"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="font-medium text-gray-800">{sd.heading}</span>
                              {sd.sub_heading ? (
                                <span className="mt-0.5 block text-xs text-gray-600">
                                  {sd.sub_heading.split('\n').map((line, k) => (
                                    <span key={k}>
                                      {line}
                                      {k < sd.sub_heading.split('\n').length - 1 ? ' ' : null}
                                    </span>
                                  ))}
                                </span>
                              ) : null}
                            </span>
                            {sd.price ? (
                              <span
                                className={`shrink-0 font-medium tabular-nums ${
                                  isCredit ? 'text-emerald-700' : 'text-gray-900'
                                }`}
                              >
                                {sd.price}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="border-t-2 border-dashed border-gray-400 bg-white px-5 py-4">
              <div className="flex justify-between gap-2">
                <span className="font-semibold text-gray-800">Total payable</span>
                <span className="text-lg font-bold tabular-nums text-gray-900">
                  {discountData.plan_selected.total_payable}
                </span>
              </div>
            </div>
          </div>

          {/* Your new plan (what they're upgrading to) */}
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
              Your new plan
            </span>
            <span className="text-sm font-medium text-gray-900">
              {discountData.plan_selected.plan}
              {discountData.plan_selected.logname ? ` · ${discountData.plan_selected.logname}` : ''}
            </span>
          </div>

          {/* Optional: previous plan for context */}
          {discountData.current_plan.plan &&
            discountData.current_plan.plan !== discountData.plan_selected.plan && (
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-2">
                <span className="text-xs font-medium text-gray-500">Previous plan</span>
                <span className="text-xs text-gray-600">{discountData.current_plan.plan}</span>
              </div>
            )}

          {/* Actions: primary and secondary clearly distinguished */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <button
              type="button"
              onClick={handleConfirmUpgrade}
              disabled={upgrading}
              className="order-2 sm:order-1 flex-1 rounded-xl bg-emerald-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {upgrading ? 'Upgrading...' : 'Confirm upgrade'}
            </button>
            <button
              type="button"
              onClick={() => setDiscountData(null)}
              className="order-1 sm:order-2 flex-1 rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50"
            >
              Change selection
            </button>
          </div>
        </>
      )}

      {(errorMessage || catalogError) && (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <strong>Error:</strong> {errorMessage || catalogError}
        </div>
      )}

      <div className="mt-2 pt-3">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50"
          aria-label="Cancel and close"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default UpgradeMembership;
